import React, { useCallback, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Button, Flex, Icon, Popover, H5 } from "orcs-design-system";
import Cropper from "react-easy-crop";
import BannerImage from "src/components/BannerImage";
import {
  LAYOUT_MODES,
  PREVIEW_POSITIONS,
} from "src/components/BannerImagePicker/consts";
import CroppedImagePreview from "./CroppedImagePreview";

const DEFAULT_TARGET_HEIGHT = 90;

const ImageCropTool = ({
  imageUrl,
  croppedAreaPixels,
  setCroppedAreaPixels,
  imageHeight = DEFAULT_TARGET_HEIGHT,
  aspectRatio = 0,
  previewPosition = PREVIEW_POSITIONS.BOTTOM,
  layoutMode = LAYOUT_MODES.BACKGROUND,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dynamicAspectRatio, setDynamicAspectRatio] = useState(aspectRatio);
  const [croppedAreaPercent, setCroppedAreaPercent] = useState(null);

  const cropSize = useMemo(() => {
    if (aspectRatio && imageHeight) {
      return { width: imageHeight * aspectRatio, height: imageHeight };
    }
    return null;
  }, [aspectRatio, imageHeight]);

  const handleMediaLoaded = useCallback(
    (mediaSize) => {
      if (!aspectRatio && imageHeight) {
        const calculatedRatio = mediaSize.width / imageHeight;
        setDynamicAspectRatio(calculatedRatio);
      }
    },
    [imageHeight, aspectRatio]
  );

  const onCropChange = useCallback((newCrop) => setCrop(newCrop), []);

  const onCropComplete = useCallback(
    (cropArea, cropPixels) => {
      setCroppedAreaPixels(cropPixels, cropArea);
      setCroppedAreaPercent(cropArea);
    },
    [setCroppedAreaPixels]
  );

  const handleZoomIn = useCallback(
    () => setZoom((z) => Math.min(z + 0.1, 5)),
    []
  );
  const handleZoomOut = useCallback(
    () => setZoom((z) => Math.max(z - 0.1, 0.5)),
    []
  );
  const handleCenterImage = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  if (!imageUrl) {
    return null;
  }

  const title =
    layoutMode === LAYOUT_MODES.AVATAR
      ? "Crop image to fit profile:"
      : "Crop image to fit banner width:";

  const boxHeight = layoutMode === LAYOUT_MODES.AVATAR ? "350px" : "300px";

  return (
    <Box flex="1" minWidth="500px">
      <H5 mt="s" mb="r" fontWeight="600">
        {title}
      </H5>
      <Flex gap="xl" alignItems="flex-start">
        <CroppedImagePreview
          layoutMode={layoutMode}
          imageUrl={imageUrl}
          cropData={croppedAreaPercent}
        />

        <Box flex="2" minWidth="380px">
          <Box
            position="relative"
            width="100%"
            height={boxHeight}
            borderRadius="4px"
            mb="s"
          >
            <Cropper
              image={imageUrl}
              crop={crop}
              cropSize={cropSize}
              zoom={zoom}
              aspect={dynamicAspectRatio}
              onCropChange={onCropChange}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              onMediaLoaded={handleMediaLoaded}
              restrictPosition={false}
              showGrid={false}
              objectFit="contain"
            />
          </Box>
          <Flex mb="s" justifyContent="end">
            <Flex justifyContent="space-between" alignItems="center" mt="r">
              <Flex gap="s">
                <Popover
                  text="Zoom out - Make the image smaller"
                  direction="top"
                  width="fit-content"
                >
                  <Button
                    onClick={handleZoomOut}
                    disabled={zoom <= 0.5}
                    aria-label="Zoom out"
                    height="35px"
                    width="35px"
                    borderRadius="35px"
                    variant="ghost"
                    iconOnly
                  >
                    <Icon icon={["fas", "minus"]} />
                  </Button>
                </Popover>
                <Popover
                  text="Center image - Reset crop position and zoom"
                  direction="top"
                  width="fit-content"
                >
                  <Button
                    onClick={handleCenterImage}
                    aria-label="Center image"
                    height="35px"
                    width="35px"
                    borderRadius="35px"
                    variant="ghost"
                    iconOnly
                  >
                    <Icon icon={["fas", "expand-arrows-alt"]} />
                  </Button>
                </Popover>
                <Popover
                  text="Zoom in - Make the image larger"
                  direction="top"
                  width="fit-content"
                >
                  <Button
                    onClick={handleZoomIn}
                    disabled={zoom >= 5}
                    aria-label="Zoom in"
                    height="35px"
                    width="35px"
                    borderRadius="35px"
                    variant="ghost"
                    iconOnly
                  >
                    <Icon icon={["fas", "plus"]} />
                  </Button>
                </Popover>
              </Flex>
            </Flex>
          </Flex>
          {croppedAreaPixels && previewPosition === "bottom" && (
            <Box mt="l">
              <H5 mb="xs" fontWeight="600">
                Preview
              </H5>
              <Box
                mb="s"
                p="m"
                borderRadius="4px"
                border="1px solid"
                borderColor="greyLight"
              >
                <BannerImage
                  imageUrl={imageUrl}
                  cropData={croppedAreaPixels}
                  bannerHeight={imageHeight}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

ImageCropTool.propTypes = {
  imageUrl: PropTypes.string,
  imageHeight: PropTypes.number,
  croppedAreaPixels: PropTypes.object,
  setCroppedAreaPixels: PropTypes.func.isRequired,
  aspectRatio: PropTypes.number,
  previewPosition: PropTypes.string,
  layoutMode: PropTypes.oneOf(["avatar", "background"]),
};

export default ImageCropTool;
